/**
 * Authentication API
 * 
 * Handles all authentication-related API calls including:
 * - Google OAuth login (session-based and JWT-based)
 * - User registration
 * - Logout
 * - Profile switching
 */

import api from './client';

export const authApi = {
  /**
  * Authenticate with Google ID token OR Access Token (Session-based)
  * Currently access token approach is used to preserve signin button UI.
  * If GoogleLogin button is used, then ID token (credentials) would be used.
  * 
  * Note: currently backend and frontend supports both approaches
  * 
  * @param { string | Object } tokenData - ID token string or object with access_token / credential
  * @returns { Promise < Object >} User data or registration required response
  */
  async loginWithGoogle(tokenData) {
    let payload = {};
    if (typeof tokenData === 'string') {
      payload = { credential: tokenData };
    } else if (tokenData.access_token) {
      payload = { access_token: tokenData.access_token };
    } else if (tokenData.credential) {
      payload = { credential: tokenData.credential };
    } else {
      throw new Error("Invalid token data provided");
    }

    const response = await api.post('/api/auth/google/', payload);
    return response.data;
  },

  /**
   * Authenticate with Microsoft access token (Session-based)
   * 
   * @param {string} accessToken - Microsoft access token
   * @returns {Promise<Object>} User data or registration required response
   */
  async loginWithMicrosoft(accessToken) {
    if (!accessToken) {
      throw new Error("Microsoft access token is missing");
    }

    const response = await api.post('/api/auth/microsoft/', {
      access_token: accessToken,
    });

    return response.data;
  },

  /**
   * Logout and destroy session (Session-based)
   * @returns {Promise<Object>} Logout confirmation
   */
  async logoutSession() {
    const response = await api.post('/api/auth/logout/');
    return response.data;
  },

  /**
   * Get current session user (Session-based)
   * @returns {Promise<Object>} Current user data
   */
  async getSessionUser() {
    const response = await api.get('/api/auth/me/');
    return response.data;
  },

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} New user data
   */
  async register(userData) {
    const response = await api.post('/api/register', userData);
    return response.data;
  },

  /**
   * Switch user profile type (CLIENT/DRIVER)
   * @param {string} desiredType - Target profile type
   * @returns {Promise<Object>} Updated user data
   */
  async switchProfile(desiredType) {
    const response = await api.patch('/api/switch_profile', { desiredType });
    return response.data;
  },
};

export default authApi;
